import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatVisitDateTime } from "@/lib/utils";
import { Users, Clock, CheckCircle2, ChevronRight, X, RefreshCw, ClipboardList, ShoppingBag, AlertTriangle } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function waitMinutes(checkedInAt) {
  if (!checkedInAt) return 0;
  return Math.round((Date.now() - new Date(checkedInAt).getTime()) / 60000);
}

export default function SaturdayQueue() {
  const location = useLocation();
  const targetCheckinId = new URLSearchParams(location.search).get("checkinId");
  const [checkins, setCheckins] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const [serveModal, setServeModal] = useState(null);
  const [highlightId, setHighlightId] = useState(targetCheckinId || null);
  const scrolledRef = useRef(false);
  const accessDeniedRef = useRef(false);
  const activeRef = useRef(true);
  const intervalRef = useRef(null);

  const handleError = useCallback((e) => {
    if (!activeRef.current) return;
    if ([401, 403].includes(e?.response?.status)) {
      accessDeniedRef.current = true;
      clearInterval(intervalRef.current);
      setCheckins([]);
      setServeModal(null);
    }
    setError(formatApiError(e));
  }, []);

  const load = useCallback(async () => {
    if (accessDeniedRef.current || !activeRef.current) return;
    try {
      const { data } = await api.get("/checkins/today");
      if (accessDeniedRef.current || !activeRef.current) return;
      setCheckins(data);
      setLastRefresh(new Date());
      setError("");
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    activeRef.current = true;
    load();
    intervalRef.current = setInterval(load, 20000);
    return () => { activeRef.current = false; clearInterval(intervalRef.current); };
  }, [load]);

  // Scroll to and highlight the target card once data is loaded
  useEffect(() => {
    if (!targetCheckinId || loading || scrolledRef.current) return;
    const el = document.querySelector(`[data-testid="queue-card-${targetCheckinId}"]`);
    if (!el) return;
    scrolledRef.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Clear highlight after 3 s
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [targetCheckinId, loading, checkins]);

  async function handleServe(checkin) {
    if (accessDeniedRef.current) return;
    const id = checkin.id;
    const isServing = checkin.status === "serving";

    if (isServing) {
      setActioning((a) => ({ ...a, [id]: "serving" }));
      try {
        await api.post(`/checkins/${id}/serve`);
        await load();
      } catch (e) { handleError(e); }
      finally { setActioning((a) => ({ ...a, [id]: null })); }
      return;
    }

    setActioning((a) => ({ ...a, [id]: "serving" }));
    try {
      await api.post(`/checkins/${id}/serve`);
      if (!accessDeniedRef.current && activeRef.current) setServeModal(checkin);
      await load();
    } catch (e) { handleError(e); }
    finally { setActioning((a) => ({ ...a, [id]: null })); }
  }

  async function handleRemove(id, name) {
    if (accessDeniedRef.current) return;
    if (!window.confirm(`Remove ${name} from the queue?`)) return;
    setActioning((a) => ({ ...a, [id]: "removing" }));
    try {
      await api.delete(`/checkins/${id}`);
      await load();
    } catch (e) { handleError(e); }
    finally { setActioning((a) => ({ ...a, [id]: null })); }
  }

  async function handleServeModalDone(checkinId) {
    setServeModal(null);
    if (accessDeniedRef.current) return;
    try {
      await api.post(`/checkins/${checkinId}/serve`);
    } catch (e) {
      handleError(e);
      setError(`Check-in recorded, but the queue entry could not be marked done automatically. Use "Mark Done" to finish. (${formatApiError(e)})`);
    }
    await load();
  }

  const waiting = checkins.filter((c) => c.status === "waiting");
  const serving = checkins.filter((c) => c.status === "serving");
  const done = checkins.filter((c) => c.status === "done");

  return (
    <div className="space-y-6" data-testid="saturday-queue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Live</div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Saturday Queue</h1>
          {lastRefresh && (
            <p className="text-sm mt-1" style={{ color: "var(--fb-text-muted)" }}>
              Updated {formatTime(lastRefresh.toISOString())} · auto-refreshes every 20s
            </p>
          )}
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
          style={{ borderColor: "var(--fb-border)" }}
          data-testid="queue-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Waiting", count: waiting.length, color: "var(--fb-accent)", bg: "var(--fb-accent)15" },
          { label: "Being Served", count: serving.length, color: "var(--fb-primary)", bg: "var(--fb-primary)15" },
          { label: "Done Today", count: done.length, color: "var(--fb-success)", bg: "var(--fb-success)15" },
        ].map(({ label, count, color, bg }) => (
          <div key={label} className="fb-card p-4 text-center">
            <div className="text-3xl font-bold tabular-nums" style={{ color, fontFamily: "Outfit" }}>{count}</div>
            <div className="text-xs font-semibold mt-1" style={{ color: "var(--fb-text-muted)" }}>{label}</div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="text-sm text-center py-8" style={{ color: "var(--fb-text-muted)" }}>Loading queue…</div>
      )}

      {!loading && checkins.filter((c) => c.status !== "done").length === 0 && (
        <div className="fb-card p-10 text-center">
          <ClipboardList className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--fb-text-muted)" }} />
          <div className="font-semibold text-lg mb-1" style={{ fontFamily: "Outfit" }}>No one in the queue yet</div>
          <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Clients will appear here after they check in via the portal.</div>
        </div>
      )}

      {/* Serving section */}
      {serving.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--fb-primary)" }}>
            Now Serving
          </div>
          {serving.map((c) => (
            <CheckInCard key={c.id} checkin={c} onServe={handleServe} onRemove={handleRemove} actioning={actioning} highlighted={c.id === highlightId} />
          ))}
        </div>
      )}

      {/* Waiting section */}
      {waiting.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#8B918B" }}>
            Waiting ({waiting.length})
          </div>
          {waiting.map((c, idx) => (
            <CheckInCard key={c.id} checkin={c} onServe={handleServe} onRemove={handleRemove} actioning={actioning} isQueueHead={idx === 0} highlighted={c.id === highlightId} />
          ))}
        </div>
      )}

      {/* Done section (collapsed) */}
      {done.length > 0 && (
        <details className="fb-card p-4">
          <summary className="text-sm font-semibold cursor-pointer" style={{ color: "var(--fb-text-muted)" }}>
            Completed today ({done.length})
          </summary>
          <div className="space-y-2 mt-3">
            {done.map((c) => (
              <CheckInCard key={c.id} checkin={c} onServe={handleServe} onRemove={handleRemove} actioning={actioning} isDone highlighted={c.id === highlightId} />
            ))}
          </div>
        </details>
      )}

      {serveModal && (
        <ServeModal
          checkin={serveModal}
          onClose={() => setServeModal(null)}
          onDone={() => handleServeModalDone(serveModal.id)}
        />
      )}
    </div>
  );
}

function CheckInCard({ checkin, onServe, onRemove, actioning, isDone, isQueueHead, highlighted }) {
  const isActioning = !!actioning[checkin.id];
  const wait = waitMinutes(checkin.checked_in_at);
  const isServing = checkin.status === "serving";
  const canCallNext = isServing || isQueueHead;

  return (
    <div
      className="fb-card p-4 flex items-start gap-4 transition-shadow"
      style={{
        opacity: isDone ? 0.6 : 1,
        ...(highlighted ? { outline: "2.5px solid var(--fb-primary)", outlineOffset: "2px" } : {}),
      }}
      data-testid={`queue-card-${checkin.id}`}
    >
      {/* Position badge */}
      {!isDone && (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-lg tabular-nums"
          style={{ background: isServing ? "var(--fb-primary)" : "var(--fb-accent)", fontFamily: "Outfit" }}
        >
          {checkin.position}
        </div>
      )}
      {isDone && (
        <CheckCircle2 className="w-8 h-8 shrink-0 mt-0.5" style={{ color: "var(--fb-success)" }} />
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-base" style={{ fontFamily: "Outfit" }}>
          {checkin.client_first_name || checkin.client_name} {checkin.client_last_name || ""}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {checkin.household_size} person{checkin.household_size !== 1 ? "s" : ""}
          </span>
          {!isDone && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {formatTime(checkin.checked_in_at)} · {wait}m wait
            </span>
          )}
        </div>
        {checkin.item_selections?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {checkin.item_selections.map((item) => (
              <span key={item} className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
                {item}
              </span>
            ))}
          </div>
        )}
        {checkin.notes && (
          <p className="text-sm mt-1.5 italic" style={{ color: "var(--fb-text-muted)" }}>"{checkin.notes}"</p>
        )}
      </div>

      {/* Actions */}
      {!isDone && (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => canCallNext && onServe(checkin)}
            disabled={isActioning || !canCallNext}
            title={!canCallNext ? "Serve the client ahead first" : undefined}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg fb-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`serve-btn-${checkin.id}`}
          >
            {isActioning ? "…" : isServing ? "Mark Done" : (
              <><ChevronRight className="w-4 h-4" /> Call Next</>
            )}
          </button>
          <button
            onClick={() => onRemove(checkin.id, checkin.client_first_name || checkin.client_name)}
            disabled={isActioning}
            className="p-2 rounded-lg hover:bg-rose-50"
            aria-label="Remove from queue"
            data-testid={`remove-btn-${checkin.id}`}
          >
            <X className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
          </button>
        </div>
      )}
    </div>
  );
}

function ServeModal({ checkin, onClose, onDone }) {
  const [bags, setBags] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [overrideWarning, setOverrideWarning] = useState(null);

  useEffect(() => {
    async function fetchBags() {
      try {
        const [resolveRes, allRes] = await Promise.all([
          api.get(`/bag-mappings/resolve?household_size=${checkin.household_size}`),
          api.get("/bags"),
        ]);
        const defaultIds = new Set((resolveRes.data.bags || []).map((b) => b.id));
        setBags(allRes.data || []);
        setSelectedIds(defaultIds);
      } catch {
        setBags([]);
      } finally {
        setLoading(false);
      }
    }
    fetchBags();
  }, [checkin.household_size]);

  function toggleBag(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function doVisit(override) {
    setSaving(true); setError("");
    try {
      await api.post(`/clients/${checkin.client_id}/visits`, {
        bag_ids: [...selectedIds],
        override,
      });
      onDone();
    } catch (e) {
      if (e.response?.status === 409 && !override) {
        setOverrideWarning(e.response.data.detail);
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  function confirm() { doVisit(false); }

  const totalWeight = bags
    .filter((b) => selectedIds.has(b.id))
    .reduce((sum, b) => sum + (b.bag_weight_lbs || 0), 0);

  const clientName = `${checkin.client_first_name || checkin.client_name || ""} ${checkin.client_last_name || ""}`.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg my-8 p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="serve-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Check In Client</h3>
            <p className="text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              {clientName} · household of {checkin.household_size}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {error && <div className="fb-status-error text-sm p-3 rounded-lg mb-4">{error}</div>}

        {/* Monthly override warning */}
        {overrideWarning && (
          <div className="space-y-4 mb-4">
            <div className="p-4 rounded-xl fb-status-warning flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">{typeof overrideWarning === "object" ? overrideWarning.message : overrideWarning}</div>
                {typeof overrideWarning === "object" && overrideWarning.last_visit && (
                  <div className="text-sm mt-1">Last visit: {formatVisitDateTime(overrideWarning.last_visit)}</div>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setOverrideWarning(null)} className="px-5 py-3 rounded-xl border font-medium text-sm" style={{ borderColor: "var(--fb-border)" }}>
                Cancel
              </button>
              <button
                data-testid="serve-modal-override-btn"
                onClick={() => { setOverrideWarning(null); doVisit(true); }}
                disabled={saving}
                className="px-5 py-3 rounded-xl font-semibold text-sm text-white disabled:opacity-60"
                style={{ background: "var(--fb-error)" }}
              >
                {saving ? "Saving…" : "Override and Check In Anyway"}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-center py-8" style={{ color: "var(--fb-text-muted)" }}>Loading bags…</div>
        ) : !overrideWarning && (
          <>
            <p className="text-sm mb-3" style={{ color: "var(--fb-text-muted)" }}>
              Select bags to distribute. Defaulted for household of {checkin.household_size}.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {bags.map((bag) => {
                const selected = selectedIds.has(bag.id);
                return (
                  <button
                    key={bag.id}
                    onClick={() => toggleBag(bag.id)}
                    data-testid={`serve-modal-bag-${bag.id}`}
                    className="flex items-center gap-3 p-4 rounded-xl border text-left transition-colors"
                    style={{
                      borderColor: selected ? "var(--fb-primary)" : "var(--fb-border)",
                      background: selected ? "var(--fb-primary)10" : "#FAFAF8",
                    }}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: selected ? "var(--fb-primary)" : "var(--fb-border)" }}>
                      <ShoppingBag className="w-4 h-4" style={{ color: selected ? "#fff" : "var(--fb-text-muted)" }} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{bag.name}</div>
                      <div className="text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>{bag.bag_weight_lbs} lbs</div>
                    </div>
                  </button>
                );
              })}
              {bags.length === 0 && (
                <div className="col-span-2 text-sm text-center py-4" style={{ color: "var(--fb-text-muted)" }}>
                  No bags configured. Go to Settings → Food Distribution to set up bags.
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 pt-3 border-t sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "var(--fb-border)" }}>
              <div className="text-sm tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                {selectedIds.size} bag{selectedIds.size !== 1 ? "s" : ""} · <span className="font-semibold">{totalWeight.toFixed(1)} lbs</span> total
              </div>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border font-medium text-sm" style={{ borderColor: "var(--fb-border)" }}>
                  Cancel
                </button>
                <button
                  data-testid="serve-modal-confirm-btn"
                  onClick={confirm}
                  disabled={saving}
                  className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl font-semibold text-sm text-white disabled:opacity-60"
                  style={{ background: "var(--fb-success)" }}
                >
                  {saving ? "Saving…" : "Confirm Check-In"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
